/**
 * PublisherDashboard — Substack-style writer hub. One page shows:
 *   • Stats tiles: articles, published, total claps, total plays,
 *     follower count
 *   • Published stories list with per-story claps + plays
 *   • Drafts list with a resume-editing link
 *   • Quick "Write a new story" CTA
 *
 * Route: /publisher/dashboard  (auth-gated in App.js)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  PenLine, Loader2, Users, Hand, Headphones, FileText, CheckCircle2,
  ArrowRight, Clock, Sparkles,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SocialCapitalChip from "@/components/publisher/SocialCapitalChip";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return ""; }
}

async function api(path, token, opts = {}) {
  const r = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
  return r.json();
}

function StatTile({ icon: Icon, label, value, accent = "teal", testId }) {
  const styles = {
    teal:   "text-teal-800 bg-teal-50 border-teal-100",
    amber:  "text-amber-800 bg-amber-50 border-amber-100",
    slate:  "text-slate-800 bg-slate-50 border-slate-200",
    rose:   "text-rose-800 bg-rose-50 border-rose-100",
  }[accent];
  return (
    <div className={`rounded-2xl border p-5 ${styles}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold opacity-80 mb-2">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="text-3xl font-bold" style={{ fontFamily: "'Lora', serif" }}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function ArticleRow({ a, isDraft }) {
  const testId = `publisher-dashboard-row-${a.slug}`;
  return (
    <div className="flex items-start gap-4 py-4 border-b border-slate-100 last:border-b-0" data-testid={testId}>
      <div className="flex-1 min-w-0">
        <Link
          to={isDraft ? `/publisher/write/${a.id}` : `/blog/${a.slug}`}
          className="block text-lg text-slate-900 hover:text-teal-700 leading-snug"
          style={{ fontFamily: "'Lora', serif", fontWeight: 600 }}
          data-testid={`${testId}-title`}
        >
          {a.title}
        </Link>
        <div className="mt-1.5 flex items-center gap-3 flex-wrap text-xs text-slate-500">
          {isDraft ? (
            <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
              <FileText className="w-3 h-3" /> Draft · saved {fmtDate(a.created_at)}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
              <CheckCircle2 className="w-3 h-3" /> Published {fmtDate(a.published_at)}
            </span>
          )}
          {a.read_time_min > 0 && (
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> {a.read_time_min} min read
            </span>
          )}
          {!isDraft && (
            <>
              <span className="inline-flex items-center gap-1">
                <Hand className="w-3 h-3" /> {a.total_claps.toLocaleString()} claps · {a.unique_clappers} clappers
              </span>
              <span className="inline-flex items-center gap-1">
                <Headphones className="w-3 h-3" /> {a.tts_play_count.toLocaleString()} plays
              </span>
            </>
          )}
        </div>
      </div>
      <Link
        to={isDraft ? `/publisher/write/${a.id}` : `/blog/${a.slug}`}
        className="shrink-0 inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-900 font-semibold"
      >
        {isDraft ? "Resume" : "View"} <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}

export default function PublisherDashboard() {
  const { user, token, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const d = await api("/writer/dashboard", token);
      setData(d);
    } catch (e) {
      setError(e.message || "Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const emptyState = useMemo(() => (
    <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center" data-testid="publisher-dashboard-empty">
      <Sparkles className="w-8 h-8 text-teal-600 mx-auto mb-3" />
      <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: "'Lora', serif" }}>
        Your first story starts here.
      </h3>
      <p className="text-sm text-slate-600 mb-5 max-w-md mx-auto">
        Publish something you care about — a place, a subject, a moment. Readers around the world will find you through the Place × Topic graph.
      </p>
      <Link
        to="/publisher/write"
        className="inline-flex items-center gap-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white px-5 py-2.5 text-sm font-semibold"
        data-testid="publisher-dashboard-write-first"
      >
        <PenLine className="w-4 h-4" /> Write your first story
      </Link>
    </div>
  ), []);

  if (authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!user) {
    const next = encodeURIComponent("/publisher/dashboard");
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  const stats = data?.stats || {};
  const drafts    = data?.drafts || [];
  const published = data?.published || [];

  return (
    <div
      className="min-h-screen"
      style={{ background: "#f8f5ef" }}
      data-testid="publisher-dashboard"
    >
      <div className="max-w-5xl mx-auto px-4 py-10">
        <header className="flex items-center justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-teal-700 font-bold">
              Publisher · Your Hub
            </div>
            <h1 className="text-4xl font-bold mt-1 text-slate-900" style={{ fontFamily: "'Lora', serif" }}>
              Hi, {data?.display_name || user?.first_name || "Writer"}.
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Your stories, drafts, readers, and reach — one page, quiet as it should be.
            </p>
          </div>
          <Link
            to="/publisher/write"
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white px-5 py-2.5 text-sm font-semibold"
            data-testid="publisher-dashboard-write"
          >
            <PenLine className="w-4 h-4" /> Write a new story
          </Link>
        </header>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your hub…
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-sm mb-4">
            {error}
          </div>
        )}

        {!loading && data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="publisher-dashboard-stats">
              <StatTile icon={FileText}  label="Articles"  value={stats.total_articles  || 0} accent="slate" testId="publisher-dashboard-stat-articles" />
              <StatTile icon={CheckCircle2} label="Published" value={stats.total_published || 0} accent="teal"  testId="publisher-dashboard-stat-published" />
              <StatTile icon={Users}     label="Followers" value={data.follower_count || 0}    accent="rose"  testId="publisher-dashboard-stat-followers" />
              <StatTile icon={Hand}      label="Claps"     value={stats.total_claps    || 0}   accent="amber" testId="publisher-dashboard-stat-claps" />
              <StatTile icon={Headphones} label="Plays"    value={stats.total_plays    || 0}   accent="slate" testId="publisher-dashboard-stat-plays" />
            </div>

            <div className="mb-8">
              <SocialCapitalChip writerId={data.writer_id} variant="full" testId="publisher-dashboard-capital" />
            </div>

            {stats.total_articles === 0 ? emptyState : (
              <div className="grid md:grid-cols-3 gap-6">
                <section className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-6" data-testid="publisher-dashboard-published">
                  <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Lora', serif" }}>
                    Published stories
                  </h2>
                  {published.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">Nothing published yet. Move a draft to publish to see it here.</p>
                  ) : (
                    <div>{published.map((a) => <ArticleRow key={a.id} a={a} isDraft={false} />)}</div>
                  )}
                </section>
                <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="publisher-dashboard-drafts">
                  <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Lora', serif" }}>
                    Drafts
                  </h2>
                  {drafts.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No drafts open. When you start writing, unfinished work lands here.</p>
                  ) : (
                    <div>{drafts.map((a) => <ArticleRow key={a.id} a={a} isDraft={true} />)}</div>
                  )}
                </section>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
